// Composable to handle guest budget transfer after authentication
export const useGuestBudgetTransfer = () => {
  const { useError } = useErrorPopup()
  const { getSavedGuestBudget, clearGuestBudget } = useGuestMode()

  /**
   * Check for saved guest budget and transfer it to authenticated user
   * Returns transfer result with success status and message
   */
  const transferGuestBudgetIfExists = async (): Promise<{
    attempted: boolean
    success: boolean
    message?: string
    existingBudget?: boolean
  }> => {
    try {
      // Check if there's a saved guest budget
      const guestBudget = getSavedGuestBudget()
      
      if (!guestBudget) {
        return {
          attempted: false,
          success: false
        }
      }      // Get current user session
      const { session } = useAuth()
      
      if (!session.value?.user?.id) {
        return {
          attempted: false,
          success: false,
          message: 'User not authenticated'
        }
      }

      // Call the transfer API
      const result = await $fetch('/api/budget/transfer-guest', {
        method: 'POST',
        body: {
          guestBudget,
          userId: session.value.user.id
        }
      })      // Handle the response
      if (result?.success) {
        // Clear the guest budget from localStorage since it's been transferred
        clearGuestBudget()
        
        return {
          attempted: true,
          success: true,
          message: (result as any).message || 'Your guest budget has been successfully transferred to your account!',
          existingBudget: false
        }
      } else if ((result as any)?.conflictType === 'budget_exists') {
        // User already has a budget for this period
        clearGuestBudget() // Clear it anyway since we can't transfer it
        
        return {
          attempted: true,
          success: false,
          message: (result as any).error || 'You already have a budget for this period.',
          existingBudget: true
        }
      } else {
        return {
          attempted: true,
          success: false,
          message: (result as any)?.error || 'Failed to transfer guest budget'
        }
      }

    } catch (error) {
      console.error('Error during guest budget transfer:', error)
      return {
        attempted: true,
        success: false,
        message: 'An unexpected error occurred while transferring your guest budget.'
      }
    }
  }

  /**
   * Show appropriate message based on transfer result
   */
  const showTransferResult = (result: {
    attempted: boolean
    success: boolean
    message?: string
    existingBudget?: boolean
  }) => {
    if (!result.attempted) {
      return // No guest budget to transfer
    }    if (result.success) {
      useError.showSuccess(result.message || 'Guest budget transferred successfully!')
    } else if (result.existingBudget) {
      useError.showError(result.message || 'You already have a budget for this period.')
    } else {
      useError.showError(result.message || 'Failed to transfer guest budget.')
    }
  }

  return {
    transferGuestBudgetIfExists,
    showTransferResult
  }
}
